/**
 * layout.js — the single source of layout truth.
 *
 * Turns a ReportDoc into a flat display list per page, in millimetres. Both
 * consumers read the SAME list:
 *
 *   caseReportDoc.js  → jsPDF   (the deliverable)
 *   PageCanvas.jsx    → canvas  (the live preview in the builder)
 *
 * That is the whole point. A WYSIWYG builder whose preview and export are two
 * separate layout implementations drifts — silently, and usually only on the
 * page nobody checked. Here they cannot: if the preview is wrong, the PDF is
 * wrong in exactly the same way, and one fix corrects both.
 *
 * This module is PURE — no jsPDF, no canvas, no DOM. Text measurement is
 * injected, because the two consumers measure differently (PDF font metrics vs
 * canvas metrics) and neither belongs in here.
 *
 * Display list ops:
 *   { op:'rect',  x,y,w,h, stroke?, fill?, lineWidth? }
 *   { op:'line',  x1,y1,x2,y2, color, width }
 *   { op:'text',  x,y, text, size, weight, align, color, maxWidth? }
 *   { op:'image', x,y,w,h, source, fit:'contain' }
 *   { op:'logo',  x,y,w,h }
 *   { op:'watermark' }
 *
 * `source` is { kind:'scene', sceneId, view } or { kind:'asset', assetId } —
 * layout never resolves pixels, it only says where they go.
 */

import { contentRight } from './geometry.js'
import { geometryFor } from './formats.js'
import { VIEW_LABELS } from './schema.js'
import { sanitize } from '../pdfChassis.js'

/**
 * Every string entering layout is sanitised HERE, not at draw time.
 *
 * caseReportDoc also sanitises before it draws — which was the whole problem:
 * layout measured "Maxilla → 4mm" and the writer drew "Maxilla -> 4mm", 1.86mm
 * wider PER ARROW. Measuring one string and drawing a different one is how text
 * wraps short and then overflows the band it was measured into, and the
 * movement records this tool is being built for are full of arrows.
 *
 * Sanitising at the boundary makes the two agree by construction. The writer's
 * pass is now idempotent, and stays as insurance for anything layout misses.
 */
const S = (v) => sanitize(v ?? '')

const INK = [0, 0, 0]
const RULE_COLOR = [0, 0, 0]
const GREY = [110, 110, 110]

/** Points → millimetres, for turning a font size into a line height. */
const ptToMm = (pt) => pt * 0.352778

/**
 * geometry.js records every header and footer rule as the **top edge** of the
 * drawn line, because that is what a raster measurement of the reference PDF
 * sees. Both jsPDF and canvas centre a stroke on its path, so the path has to
 * sit half a line-width lower or every rule lands 0.25 mm high. The content
 * frame is the exception: its rules are recorded as centres and gridOps draws
 * its paths on them.
 */
const ruleY = (rule) => rule.y + (rule.width ?? 0.2) / 2

/**
 * @param {object} doc          a ReportDoc
 * @param {object} opts
 * @param {(text:string, size:number, weight:string) => number} opts.measureText
 *        width of `text` in mm. Required — layout cannot wrap without it.
 * @param {object} [opts.geometry] override the template's geometry (tests)
 * @returns {Array<{page:object, index:number, total:number, ops:Array}>}
 */
export function layoutReport(doc, { measureText, geometry } = {}) {
  if (typeof measureText !== 'function') {
    throw new TypeError('layoutReport: measureText is required — layout cannot wrap text without metrics')
  }
  const g = geometry ?? geometryFor(doc.template)
  if (!g) throw new Error(`layoutReport: no geometry for the "${doc.template}" format`)

  // Flow FIRST, then number. "Page 03 of 23" has to count the pages that will
  // actually exist, so a body that spills onto a continuation page changes the
  // denominator on every page of the document.
  const flowed = flowTextPages(doc, g, measureText)
  const total = flowed.length
  return flowed.map((page, index) => ({
    page, index, total,
    ops: layoutPage(doc, page, index, total, g, measureText),
  }))
}

/**
 * Expand any text page whose body does not fit into as many pages as it needs.
 *
 * Non-text blocks pass through untouched. A continuation page keeps the
 * original's captions and frame and gets a derived id, so nothing downstream has
 * to special-case it.
 */
function flowTextPages(doc, g, measureText) {
  const out = []
  for (const page of doc.pages) {
    if (page.block !== 'text') { out.push(page); continue }
    // The band depends on the header, which grows when a long hospital name
    // wraps — so measure against the SAME band layoutPage will use.
    const { shift } = headerOps(doc, g, measureText)
    const gs = shift ? shiftBelowHeader(g, shift) : g
    const band = captionOps(page, gs, [], measureText)
    const chunks = splitBody(page.body ?? [], band, measureText, page.styles ?? null)
    if (chunks.length <= 1) { out.push(page); continue }
    for (const [i, body] of chunks.entries()) {
      out.push(i === 0 ? { ...page, body } : { ...page, id: `${page.id}-c${i}`, body })
    }
  }
  return out
}

function layoutPage(doc, page, index, total, g, measureText) {
  const ops = []
  // THE WATERMARK GOES FIRST, under everything.
  //
  // 🔴 30 Sep 2026. It used to go on top of the artwork, drawn at 17 % alpha,
  // because a render is an opaque JPEG and a watermark under one is erased
  // rather than faint. That alpha cost the page its vector lines in print: one
  // transparent object spanning the whole sheet makes every print path that
  // cannot composite rasterise the WHOLE page — measured, `pdftops -level2` on
  // the team's own file gave one 2480×3507 raster and zero vector strokes, so
  // the frame arrived at the printer as halftoned pixels. Three width changes
  // were spent on that symptom. pdfChassis.overWhite carries the measurement.
  //
  // The artwork is not losing its watermark: every picture now has the lattice
  // stamped into its own pixels at the true 17 % (pdfChassis.stampWatermark),
  // from the SAME lattice this op draws, so the page looks as it always did.
  // What changed is that the page no longer needs transparency to do it.
  //
  // First, not last, for a second reason: drawn last it is opaque now, and an
  // opaque tile crossing a 0.254 mm rule would punch a grey gap in it — the
  // very "broken borders" being fixed.
  //
  // Stated deviations, measured and accepted. Anything OPAQUE drawn after this
  // op now covers the tiles beneath it, where they used to cross it:
  //   - the logo's box (57.49 x 16.51 mm, about two tiles). The alternative was
  //     to stamp the logo like a picture, which forces a JPEG — canvas PNG
  //     always carries an alpha channel and a soft mask there rasterises the
  //     whole sheet. Measured on the asset: even JPEG q0.98 moves pixels by 9
  //     and doubles the file. The brand mark stays a lossless PNG instead.
  //   - the cephalometry table's header band and the colour legend's swatches,
  //     neither of which is on a page the team prints today.
  // A rule or a glyph crossed by a tile is now pure black instead of lightened
  // to rgb(24,25,26). That is the improvement, not a deviation.
  //
  // Pinned by the paint-order tests in layout.test.js. It has to be pinned:
  // the original fix was written once and sat unmerged on a branch while the
  // deployed report shipped without a watermark, and nothing failed to say so.
  ops.push({ op: 'watermark', ...g.watermark })
  const { ops: header, shift } = headerOps(doc, g, measureText, page)
  ops.push(...header)
  // A wrapped identity value grows the header, so everything between it and the
  // footer moves down with it. The footer is pinned to the page bottom, so the
  // content band absorbs the difference.
  const gs = shift ? shiftBelowHeader(g, shift) : g
  const band = captionOps(page, gs, ops, measureText)
  ops.push(...blockOps(doc, page, gs, band, measureText))
  ops.push(...annotationOps(page.annotations ?? [], gs))
  // The watermark was here until 30 Sep 2026; it is now the FIRST op of the
  // page — see the note at the top of this function. Its parameters still
  // travel with the template rather than the shared helper, because the case
  // report's lattice is denser than the SSE report's (measured, not eyeballed).
  ops.push(...footerOps(doc, g, index, total))
  return ops
}

/**
 * Push the caption ring and the content band down by `shift` mm, leaving the
 * footer where it is.
 *
 * Without this a two-line hospital name printed straight through the top
 * caption — the same class of defect the SSE report shipped in July 2026, where
 * a long hospital name overprinted the Case ID. Fixed baselines are fine until
 * real data arrives; the reference PDFs never wrapped because the demo case had
 * short names.
 */
function shiftBelowHeader(g, shift) {
  return {
    ...g,
    ruleUnderInfo: { ...g.ruleUnderInfo, y: g.ruleUnderInfo.y + shift },
    captionTop: { ...g.captionTop, baseline: g.captionTop.baseline + shift },
    content: { ...g.content, top: g.content.top + shift },
  }
}

// ── page chrome ──────────────────────────────────────────────────────────────

function headerOps(doc, g, measureText, page = null) {
  const ops = []
  const right = contentRight(g)
  const cx = g.page.w / 2

  // The logo PNG carries the "3d printing healthcare®" tagline, so it is one
  // image, not a wordmark plus a text line.
  // A geometry that states the logo's own box (the master) draws it there, at its width; the
  // measured templates centre it and let the asset's aspect decide the width.
  ops.push(g.logo.x != null && g.logo.width != null
    ? { op: 'logo', x: g.logo.x + g.logo.width / 2, y: g.logo.top, w: g.logo.width, h: g.logo.height }
    : { op: 'logo', x: cx, y: g.logo.top, w: null, h: g.logo.height })

  // A page may override the SECOND line. The orbital report's page 1 reads
  // "Preview Image" where pages 2-6 read "Images for Design Confirmation", on the
  // same baseline and centre — measured, and a real per-page difference rather
  // than a second line. The footer's product line still uses meta.titleLine2, so
  // the override changes the header only, which is what the reference does.
  // Trimmed: a chrome string of one space is not a header line, and left untrimmed
  // it drew a blank on line 1 and pushed the real title back down to line 2 — the
  // very gap this rule removes.
  const line1 = S(doc.meta.titleLine1).trim()
  const line2 = S(page?.headerTitle ?? doc.meta.titleLine2).trim()
  // ONE line sits BETWEEN the two baselines, not on the lower one.
  //
  // A template may state one header line — planning images declares
  // `titleLine1: { text: '', note: 'this template has one header line' }` — and
  // drawing that single line on line 2 left line 1's slot empty, which reads as a
  // missing line under the logo (the team, 1 Oct). The block keeps its height
  // either way, so the rule under the title and everything below it stay put. No
  // second template: the format data already says how many lines it has.
  const alone = Boolean(line1) !== Boolean(line2)
  // The geometry states where one line goes — measured off the delivered one-line
  // document, not derived from the two-line baselines (template.js title).
  const solo = g.title.oneLineBaseline ?? (g.title.line1Baseline + g.title.line2Baseline) / 2
  if (line1) {
    ops.push({
      op: 'text', x: cx, y: alone ? solo : g.title.line1Baseline, text: line1,
      size: g.title.size, weight: 'normal', align: 'center', color: INK,
    })
  }
  if (line2) {
    ops.push({
      op: 'text', x: cx, y: alone ? solo : g.title.line2Baseline, text: line2,
      size: g.title.size, weight: 'normal', align: 'center', color: INK,
    })
  }
  // DK-1 (template.js): no version is printed anywhere on the page — it lives in the PDF
  // properties. Nothing else goes on the two title lines.
  ops.push({
    op: 'line', x1: g.margin, y1: ruleY(g.ruleUnderTitle), x2: right, y2: ruleY(g.ruleUnderTitle),
    color: RULE_COLOR, width: g.ruleUnderTitle.width,
  })

  const info = infoBarOps(doc.meta, g, measureText)
  ops.push(...info.ops)

  const rule = { ...g.ruleUnderInfo, y: g.ruleUnderInfo.y + info.shift }
  ops.push({
    op: 'line', x1: g.margin, y1: ruleY(rule), x2: right, y2: ruleY(rule),
    color: RULE_COLOR, width: rule.width,
  })
  return { ops, shift: info.shift }
}

/** The four identity labels, as the template spells them. */
const INFO_LABELS = {
  // Lower-case "name", upper-case "ID" — as delivered. The reference report
  // reads "Surgeon name:" / "Case ID:", and these four labels appear on every
  // page of every report, so a capitalisation drift here is the most-repeated
  // visible difference between our output and the document the team ships.
  surgeonLabel: ['Surgeon name', 'surgeonName'],
  hospitalLabel: ['Hospital name', 'hospitalName'],
  patientLabel: ['Patient name', 'patientName'],
  caseIdLabel: ['Case ID', 'caseId'],
}

/**
 * The identity bar as ONE column of rows, on the baselines the template states.
 *
 * The orthognathic V1.3.5 replaced its 2×2 block with four rows in a single
 * column, because the team's doctor and hospital names were wrapping inside a
 * 97 mm half: "Dr S Sindhuja Devi / Dr Divakar / Dr Georgeno" and "Rajas Dental
 * College and Hospital, Kavalkinaru" each took two lines. A full-width value has
 * 195 mm, so both fit on one.
 *
 * The colons are aligned in a column, which is what the template does by padding
 * each label ('Case ID            : '). Padding is not reproduced — it would need
 * GIMP's own metrics for Mulish Medium while this draws in the regular face.
 * Instead the colon is placed at the widest label's own measured width, which
 * aligns by construction rather than by a string that happens to be the right
 * length.
 *
 * Values still wrap, and still push what follows down — a name long enough to
 * wrap at 195 mm must not print through the rule under the block.
 */
function infoColumnOps(meta, g, measureText) {
  const ops = []
  const rows = g.info.rows
  const size = g.info.size
  const gutter = 3
  const labelOf = (key) => INFO_LABELS[key]?.[0] ?? key
  // The MASTER (template.js, 26 Sep): each label carries its own colon — `Surgeon name: ` — and
  // the value follows it on the line. Nothing is aligned across rows.
  if (g.info.colonAttached) {
    let shift = 0
    for (const row of rows) {
      const [label, metaKey] = INFO_LABELS[row.key] ?? [row.key, null]
      const head = `${label}: `
      const headW = measureText(head, size, 'normal')
      const y = row.baseline + shift
      ops.push({ op: 'text', x: row.x, y, text: head, size, weight: 'normal', align: 'left', color: INK })
      const avail = Math.max(contentRight(g) - row.x - headW - gutter, 12)
      const lines = wrap(String(metaKey ? S(meta[metaKey]) : ''), avail, size, 'normal', measureText)
      for (const [li, line] of lines.entries()) {
        ops.push({
          op: 'text', x: row.x + headW, y: y + li * g.info.lineH, text: line,
          size, weight: 'normal', align: 'left', color: INK,
        })
      }
      shift += Math.max(0, lines.length - 1) * g.info.lineH
    }
    return { ops, shift }
  }
  // One colon column for the whole block: the widest label decides it.
  const colonX = Math.max(...rows.map((row) =>
    row.x + measureText(labelOf(row.key), size, 'normal'))) + 1.5
  const valueX = colonX + measureText(': ', size, 'normal')

  let shift = 0
  for (const row of rows) {
    const [label, metaKey] = INFO_LABELS[row.key] ?? [row.key, null]
    const y = row.baseline + shift
    ops.push({ op: 'text', x: row.x, y, text: label, size, weight: 'normal', align: 'left', color: INK })
    const avail = Math.max(contentRight(g) - valueX - gutter, 12)
    const value = metaKey ? S(meta[metaKey]) : ''
    const lines = wrap(String(value ?? ''), avail, size, 'normal', measureText)
    // The colon travels with the VALUE, as one text object per row. Drawn as its
    // own op it aligned just as well but split the row in the PDF's text layer —
    // "Patient name" then ":" then the name — so anything reading the document
    // back, including a copy-paste, saw three fragments where the delivered
    // report has one line.
    for (const [li, line] of (lines.length ? lines : ['']).entries()) {
      ops.push({
        op: 'text', x: li === 0 ? colonX : valueX, y: y + li * g.info.lineH,
        text: li === 0 ? `: ${line}` : line,
        size, weight: 'normal', align: 'left', color: INK,
      })
    }
    shift += Math.max(0, lines.length - 1) * g.info.lineH
  }
  return { ops, shift }
}

/**
 * The 2×2 identity bar. Values WRAP inside their half — the same defect the SSE
 * report was bitten by in July 2026, where a long hospital name printed straight
 * through the Case ID. Wrapping is why measureText has to be injected.
 */
function infoBarOps(meta, g, measureText) {
  // A template that states a single column of rows is drawn from those rows.
  // The ten two-column templates fall through to the 2×2 block below, unchanged.
  if (g.info.rows?.length) return infoColumnOps(meta, g, measureText)
  const ops = []
  const rows = [
    // Lower-case "name", upper-case "ID" — as delivered. The reference report
    // reads "Surgeon name:" / "Case ID:", and these four labels appear on every
    // page of every report, so a capitalisation drift here is the most-repeated
    // visible difference between our output and the document the team ships.
    [['Surgeon name', S(meta.surgeonName)], ['Patient name', S(meta.patientName)]],
    [['Hospital name', S(meta.hospitalName)], ['Case ID', S(meta.caseId)]],
  ]
  const baselines = [g.info.row1Baseline, g.info.row2Baseline]
  const colX = [g.margin, g.info.rightColX]
  const colEnd = [g.info.rightColX, contentRight(g)]
  const gutter = 3

  // Row 2 moves down by whatever row 1 overflowed, and the block reports the
  // total so the caller can push the rest of the page down too.
  let shift = 0
  rows.forEach((row, ri) => {
    let rowExtra = 0
    row.forEach(([label, value], ci) => {
      const labelText = `${label}: `
      const labelW = measureText(labelText, g.info.size, 'normal')
      const y = baselines[ri] + shift
      ops.push({
        op: 'text', x: colX[ci], y, text: labelText,
        size: g.info.size, weight: 'normal', align: 'left', color: INK,
      })
      const avail = Math.max(colEnd[ci] - colX[ci] - labelW - gutter, 12)
      const lines = wrap(String(value ?? ''), avail, g.info.size, 'normal', measureText)
      for (const [li, line] of lines.entries()) {
        ops.push({
          op: 'text', x: colX[ci] + labelW, y: y + li * g.info.lineH,
          text: line, size: g.info.size, weight: 'normal', align: 'left', color: INK,
        })
      }
      rowExtra = Math.max(rowExtra, (lines.length - 1) * g.info.lineH)
    })
    shift += rowExtra
  })
  return { ops, shift }
}

function footerOps(doc, g, index, total) {
  const ops = []
  const right = contentRight(g)
  const f = g.footer
  // The footer text sits inside the rules, not flush with them — 2.97 mm on the
  // orthognathic report, 0 on the fibula one. Measured, not assumed.
  const fLeft = g.margin + (f.inset ?? 0)
  const fRight = right - (f.inset ?? 0)
  const product = S(`Osteo3d® ${doc.meta.productName ?? doc.meta.titleLine2} ${doc.meta.reportVersion}`).trim()

  ops.push({
    op: 'line', x1: g.margin, y1: ruleY(g.ruleFooter), x2: right, y2: ruleY(g.ruleFooter),
    color: RULE_COLOR, width: g.ruleFooter.width,
  })
  // The house template (template.js, DK-1) carries no product line: the version
  // moved to the header. Only a geometry stating `showProductLine: false` skips
  // it; every measured template still prints it.
  if (f.showProductLine !== false) {
    ops.push({
      op: 'text', x: fLeft, y: f.productBaseline, text: product,
      size: f.productSize, weight: 'normal', align: 'left', color: INK,
    })
  }
  // The report may switch numbering OFF while the team's workflow is HYBRID —
  // some pages from this tool, some built by hand and inserted afterwards, which
  // makes the tool's own count wrong the moment it happens. The geometry constant
  // stays `true` because it is the measured record of the delivered reference
  // report, which does carry numbers; it must not be falsified to express a
  // preference. Undefined falls through to that constant, so nothing else moves.
  if (doc.meta?.showPageNumbers ?? f.showPageNumbers) {
    ops.push({
      // The house template (template.js, DK-1) has no product line, so its page
      // number states its own baseline and size; every measured template keeps
      // sharing the product line's.
      // The MASTER states its slot's own x and anchor (left at 177.29); every measured
      // template right-aligns at the content edge.
      op: 'text', x: f.pageNumberX ?? fRight, y: f.pageNumberBaseline ?? f.productBaseline,
      // The orbital reference pads the total too ("Page 01 of 06"); the
      // orthognathic one does not ("of 23"). Per-template, measured.
      text: `Page ${String(index + 1).padStart(2, '0')} of `
        + (f.padTotal ? String(total).padStart(2, '0') : String(total)),
      size: f.pageNumberSize ?? f.productSize, weight: 'normal',
      align: f.pageNumberAlign ?? 'right', color: INK,
    })
  }
  if (f.showNotToScale && f.notToScaleBaseline != null) {
    // The template's own layer text, split on its tab run. TWO phrases are
    // drawn at the measured ink edges — first left-aligned at `inkLeft`, second
    // right-aligned at `inkRight` — which is what reproduces a tab-separated
    // layer whose tab stops the XCF does not preserve. ONE phrase is centred,
    // exactly as before, so the eleven single-phrase templates do not move.
    //
    // A template that measured no text at all falls back to the phrase every
    // one of the twelve carries, rather than printing nothing.
    const parts = f.notToScaleParts?.length ? f.notToScaleParts : ['Images are not to scale']
    const notToScale = (text, x, align) => ops.push({
      op: 'text', x, y: f.notToScaleBaseline, text,
      size: f.notToScaleSize, weight: 'bold', align, color: INK,
    })
    if (parts.length >= 2 && f.notToScaleInkLeft != null && f.notToScaleInkRight != null) {
      notToScale(parts[0], f.notToScaleInkLeft, 'left')
      notToScale(parts[parts.length - 1], f.notToScaleInkRight, 'right')
    } else {
      notToScale(parts[0], g.page.w / 2, 'center')
    }
  }
  // The year belongs to the report, not to the build — a 2025 case regenerated
  // in 2027 must still carry its own year.
  const year = doc.meta.copyrightYear ?? '2026'
  // A geometry may state its own lines (the MASTER breaks after "trademarks."), its weight
  // (the master's block is Mulish Medium) and its colour (black). The measured templates keep
  // the house lines, regular, grey.
  const copyright = f.copyrightLines
    ? f.copyrightLines.map((l) => l.replace('{year}', year))
    : [
      `Copyright © ${year} Osteo3d®. All rights reserved. 'Osteo3d®' and '3d printing healthcare®' are registered`,
      'trademarks. Unauthorized use/replication is prohibited. We do not have any representatives or distributors',
    ]
  const smallWeight = f.copyrightWeight ?? 'normal'
  const smallColor = f.copyrightInk ? INK : GREY
  f.copyrightBaselines.forEach((y, i) => {
    ops.push({
      op: 'text', x: g.page.w / 2, y, text: copyright[i] ?? '',
      size: f.copyrightSize, weight: smallWeight, align: 'center', color: smallColor,
    })
  })
  // A template that carries no phone/email line prints none (preview images).
  if (f.contactBaseline != null) {
    if (f.contactParts?.length >= 2 && f.contactInkLeft != null && f.contactInkRight != null) {
      // The MASTER's third line: phone left-aligned and email right-aligned at the measured ink
      // edges — the same construction as the not-to-scale line above.
      ops.push({
        op: 'text', x: f.contactInkLeft, y: f.contactBaseline, text: f.contactParts[0],
        size: f.contactSize, weight: smallWeight, align: 'left', color: smallColor,
      })
      ops.push({
        op: 'text', x: f.contactInkRight, y: f.contactBaseline, text: f.contactParts[f.contactParts.length - 1],
        size: f.contactSize, weight: smallWeight, align: 'right', color: smallColor,
      })
    } else {
      ops.push({
        op: 'text', x: g.page.w / 2, y: f.contactBaseline,
        text: 'Phone: +91-9900265669          Email: contactus@Osteo3d.com',
        size: f.contactSize, weight: smallWeight, align: 'center', color: smallColor,
      })
    }
  }
  return ops
}

/**
 * Draw the caption ring and return the vertical band left for the block.
 *
 * A missing caption gives its space back to the content — an image-only page
 * should not carry a blank strip where a caption would have been.
 */
function captionOps(page, g, ops, measureText = null) {
  const c = page.captions ?? {}
  const cx = g.page.w / 2
  const right = contentRight(g)

  /**
   * Shrink a caption that would overrun the content width.
   *
   * ADR 0009: the orbital reference varies its own caption size and gave the
   * LONGEST caption the smallest one, i.e. it shrank by hand to fit. We author
   * one size and shrink only when we must, which keeps short captions identical
   * across pages and stops a 148 mm caption running into the margins.
   *
   * Only for templates whose geometry asks (`captionTop.fit`), so the two
   * shipped templates cannot move — their golden hashes are the proof.
   */
  const fitSize = (text, base) => {
    if (!g.captionTop.fit || !measureText || !text) return base
    const avail = g.contentW
    // 0.25 pt steps, floor at a fraction of the authored size — below that the
    // caption is unreadable and the right answer is a shorter caption, not
    // smaller type. The floor is a real floor: stepping while the guard holds
    // can overshoot it by one step, so it is clamped rather than merely tested.
    //
    // 70% by default, but PER-GEOMETRY, because "unreadable" is a judgement the
    // team has already made per template. The fibula report carries two captions
    // that are whole sentences and sets them at roughly 8 pt against a 14.4 pt
    // standard — 56% — so a 70% floor would leave its page 9 and 15 caption
    // overrunning the column by 44 mm while reporting itself as fitted.
    const floor = base * (g.captionTop.fitFloor ?? 0.7)
    let size = base
    while (size - 0.25 >= floor && measureText(text, size, 'normal') > avail) size -= 0.25
    return Math.max(floor, size)
  }
  // An `info` page draws its heading INSIDE the content box (see infoOps), where
  // the reference has it. Drawing `tc` here as well printed the heading TWICE —
  // once on the frame rule, once in the box — and stole a caption strip's worth
  // of height from the content for the copy nobody wanted. So the top-centre
  // slot belongs to infoOps on this block, and to the caption ring on every
  // other one.
  const ownsTopCentre = page.block !== 'info'
  // Under the master's source-data layout the caption line carries the case ID and the
  // report date (sourceDataOps), so the bottom slots are not drawn there — they would
  // overprint (G3 review, 28 Sep).
  const ownsBottom = !(page.block === 'info' && g.sourceData)
  const hasTop = Boolean((ownsTopCentre && c.tc) || c.tl || c.tr)
  const hasBottom = ownsBottom && Boolean(c.bc || c.bl || c.br)

  const slot = (text, x, align, y, size) => {
    if (!text) return
    ops.push({ op: 'text', x, y, text, size: fitSize(text, size), weight: 'normal', align, color: INK })
  }

  // 🔴 A LEFT/RIGHT caption pair labels the two COLUMNS, so it is centred on
  // them — not pushed out to the margins.
  //
  // These two slots were drawn left-aligned at the margin and right-aligned at
  // the content edge, which put "Native skull" hard against the left rule and
  // "Post plan skull" hard against the right one. The template centres them on
  // its own columns: measured on pages 7 and 8, "Post plan skull" has a box
  // centre of 153.966 mm against the right column's centre of 153.965.
  //
  // Only when the template STATES its columns and the page carries BOTH
  // captions. One caption on its own is not a column label — page 3 puts a lone
  // "Native skull" on the content centre — and a template with no measured
  // column rules keeps the margins it had.
  const centres = g.contentColCentres
  const pairOverColumns = Boolean(c.tl && c.tr && centres?.length === 2)
  slot(S(c.tl), pairOverColumns ? centres[0] : g.margin,
    pairOverColumns ? 'center' : 'left', g.captionTop.baseline, g.captionTop.size)
  if (ownsTopCentre) slot(S(c.tc), cx, 'center', g.captionTop.baseline, g.captionTop.size)
  slot(S(c.tr), pairOverColumns ? centres[1] : right,
    pairOverColumns ? 'center' : 'right', g.captionTop.baseline, g.captionTop.size)

  // Sub-caption stack ("Proximal", "Units = mm") sits under the top caption and
  // eats into the content band.
  const subLineH = ptToMm(g.captionTop.size) * 1.25
  let subBottom = g.captionTop.baseline
  for (const [i, line] of (c.sub ?? []).filter(Boolean).entries()) {
    const y = g.captionTop.baseline + (i + 1) * subLineH
    ops.push({
      op: 'text', x: cx, y, text: line,
      size: g.captionTop.size, weight: 'normal', align: 'center', color: INK,
    })
    subBottom = y
  }

  if (ownsBottom) {
    slot(S(c.bl), g.margin, 'left', g.captionBottom.baseline, g.captionBottom.size)
    slot(S(c.bc), cx, 'center', g.captionBottom.baseline, g.captionBottom.size)
    slot(S(c.br), right, 'right', g.captionBottom.baseline, g.captionBottom.size)
  }

  const subExtra = (c.sub ?? []).filter(Boolean).length ? subBottom + 2 - g.content.top : 0
  // 🔴 A template that DRAWS its frame keeps the band at that frame, whatever
  // captions the page carries.
  //
  // This used to read `hasTop ? g.content.top : g.ruleUnderInfo.y + 3` and
  // `hasBottom ? g.content.bottom : g.ruleFooter.y - 3`, so a page with no
  // bottom caption pushed its grid 2.67 mm lower than a page with one
  // (269.62 - 3 against 263.95 on the orthognathic) and every page's image cells
  // were a slightly different height. The team reported exactly that on
  // 2026-09-25, in two of their seven items.
  //
  // The template does not work that way: its eight grid layers agree to the
  // last measured place and `sharedGridDrift` is empty. A caption belongs in its
  // own measured slot OUTSIDE the frame — which is where `captionTop.baseline`
  // and `captionBottom.baseline` already put it — not in space taken from the
  // images.
  //
  // The `± 3` fallback survives for the three templates that draw no frame at
  // all; there, the band genuinely has to be found between the rules.
  const stated = g.contentFrameStated
  // A stated frame runs between its own column rules (7.75 → 202.73 on the master); the text
  // margin (7.7 → 202.7) is the header and footer rules' span.
  const fx = stated && g.contentCols?.length ? g.contentCols[0] : g.margin
  const fw = stated && g.contentCols?.length ? g.contentCols[g.contentCols.length - 1] - fx : g.contentW
  return {
    x: fx,
    w: fw,
    top: (stated || hasTop ? g.content.top : g.ruleUnderInfo.y + 3) + Math.max(0, subExtra),
    bottom: stated || hasBottom ? g.content.bottom : g.ruleFooter.y - 3,
  }
}

// ── blocks ───────────────────────────────────────────────────────────────────

function blockOps(doc, page, g, band, measureText) {
  switch (page.block) {
    case 'grid': return gridOps(page, g, band)
    case 'text': return textOps(page, g, band, measureText)
    case 'info': return infoOps(page, g, band, measureText, doc)
    case 'ceph': return cephOps(page, g, band, measureText)
    case 'steps': return stepsOps(page, g, band, measureText)
    case 'measure': return measureOps(page, g, band, measureText)
    case 'gallery': return galleryOps(page, g, band, measureText)
    default:
      // A block the writer does not know yet must be visible as a gap, never
      // silently skipped — a missing page in a surgical document is worse than
      // an obviously unfinished one.
      return [{
        op: 'text', x: g.page.w / 2, y: (band.top + band.bottom) / 2,
        text: `[${page.block} block not implemented]`,
        size: 11, weight: 'normal', align: 'center', color: GREY,
      }]
  }
}

/**
 * The cephalometric analysis page — the planning engine's numbers, VERBATIM.
 *
 * Everything here is a pass-through of `meta.cephalometry` from the engine's `report.json`: the
 * classic trio with its reading sentences, the Burstone (COGS) table with each value's published
 * band and verdict, the chosen norm set WITH its citation, and the frontal comparative rows when
 * they exist. Nothing is recomputed or re-rounded — the report is read later, elsewhere, by
 * someone who was not in the room when the landmarks were placed, so it must carry exactly the
 * measurement the operator saw.
 *
 * 🔴 Absences are stated, never blank. No norm set on the frontal rows is Grummons' design (the
 * patient is their own control) and the note says so; locked/refused counts appear when they are
 * non-zero, so a short table cannot read as a complete analysis.
 */
function cephOps(page, g, band, measureText) {
  const ops = []
  const c = page.ceph ?? {}
  const size = 7.5
  const lineH = ptToMm(size) * 1.3
  let y = band.top + lineH

  const text = (x, str, opts = {}) => ops.push({
    op: 'text', x, y, text: S(str),
    size: opts.size ?? size, weight: opts.weight ?? 'normal',
    align: opts.align ?? 'left', color: opts.color ?? INK,
  })

  y = bandHeading(ops, page, { x: band.x, y, size, fallback: 'Cephalometric analysis', advance: lineH * 1.5 })

  // ── the classic trio, as sentences ─────────────────────────────────────────
  // May be legitimately absent (trio landmarks refused/locked while COGS or frontal measurements
  // are confirmed — see hasCephAnalysis); the rest of the analysis still renders.
  for (const r of c.read ?? []) {
    text(band.x, r.sentence ?? `${r.name} ${r.value}`, { size: size + 1 })
    y += lineH * 1.1
  }
  const cogs = c.cogs
  // 🔴 Gated on `change` ALONE — review finding. This used to also require
  // `cogs?.timepoint === 'pre-op-and-planned'`, but the producer's own design emits `change`
  // with NO cogs block when no norm set was chosen ("No choice ⇒ no cogs block, and the classic
  // trio above still travels on its own"), and the planned SNA/SNB/ANB then silently vanished —
  // a page reading as if the plan changed nothing. `change` existing IS the statement that the
  // plan moved the skeleton; the COGS timepoint governs only the table's After column below.
  const after = c.change?.after
  // `after` can be legitimately absent while `change` exists — the producer models a REFUSED
  // planned-recomputation (afterRefused) — and printing "SNA undefined" in bold on a surgical
  // document is worse than printing nothing. All three or none.
  if (after && after.SNA != null && after.SNB != null && after.ANB != null) {
    text(band.x, `After the planned movements: SNA ${after.SNA} / SNB ${after.SNB} / ANB ${after.ANB}`,
         { size: size + 1, weight: 'bold' })
    y += lineH * 1.1
  }
  y += lineH * 0.5

  // ── the Burstone (COGS) table ──────────────────────────────────────────────
  if (cogs?.measured?.length) {
    const planned = cogs.timepoint === 'pre-op-and-planned'
    const afterRefusedKeys = new Set((cogs.afterRefused ?? []).map((r) => r.key))
    // Column x-origins across the band. The label column takes what remains.
    const wVal = 14, wNorm = 24, wVerd = 12, wAfter = planned ? 26 : 0
    const xAfter = band.x + band.w - wAfter
    const xVerd = xAfter - wVerd
    const xNorm = xVerd - wNorm
    const xVal = xNorm - wVal
    const labelW = xVal - band.x - 3
    const rowH = 3.7

    // Header row over a light fill, ruled beneath — the sseReport table look,
    // lowered to ops.
    ops.push({ op: 'rect', x: band.x - 1, y: y - rowH * 0.72, w: band.w + 2, h: rowH,
               fill: [235, 238, 241] })
    text(band.x, 'Measurement', { weight: 'bold' })
    text(xVal, 'Value', { weight: 'bold' })
    text(xNorm, 'Norm', { weight: 'bold' })
    text(xVerd, 'Verdict', { weight: 'bold' })
    if (planned) text(xAfter, 'After the plan', { weight: 'bold' })
    y += rowH * 0.55
    ops.push({ op: 'line', x1: band.x - 1, y1: y, x2: band.x + band.w + 1, y2: y, width: 0.3 })
    y += rowH * 0.9

    let group = null
    for (const m of cogs.measured) {
      if (m.group !== group) {
        group = m.group
        y += rowH * 0.15
        text(band.x, group ?? '', { weight: 'bold', color: GREY })
        y += rowH * 0.95
      }
      // The label may outrun its column; it wraps rather than overprinting the
      // value — the overflow defect this file keeps being bitten by.
      const lines = wrap(S(m.label ?? m.key), labelW, size, 'normal', measureText)
      text(band.x, lines[0])
      for (let i = 1; i < lines.length; i++) {
        ops.push({ op: 'text', x: band.x, y: y + i * rowH * 0.86, text: lines[i],
                   size, weight: 'normal', align: 'left', color: INK })
      }
      text(xVal, `${m.value}${m.unit === 'deg' ? ' deg' : ' ' + (m.unit ?? '')}`)
      text(xNorm, m.norm ? `${m.norm.mean} +/- ${m.norm.sd ?? '-'}` : 'no published value',
           { color: m.norm ? INK : GREY })
      if (m.verdict) text(xVerd, m.verdict, { weight: m.verdict === 'normal' ? 'normal' : 'bold' })
      if (planned && !m.after && afterRefusedKeys.has(m.key)) {
        // The producer names these rows PRECISELY so a blank cell cannot read as "unchanged by
        // the plan" (re-review finding: afterRefused was emitted and never consumed).
        text(xAfter, 'not recomputable', { color: GREY })
      }
      if (planned && m.after) {
        const d = m.after.delta
        const delta = d === 0 ? '' : ` (${d > 0 ? '+' : ''}${d})`
        text(xAfter, `${m.after.value}${delta}`)
      }
      y += rowH * Math.max(1, lines.length * 0.86 + 0.14)
    }
    y += lineH * 0.4

    // The set the values were read against, WITH its citation — a norm without
    // its source is a number wearing authority it cannot show.
    if (cogs.set) {
      const cite = `Read against ${cogs.set.label} (${cogs.set.sex}) - ${cogs.set.citation}`
      for (const ln of wrap(S(cite), band.w, size - 1, 'normal', measureText)) {
        text(band.x, ln, { size: size - 1, color: GREY })
        y += lineH * 0.75
      }
      // 🔴 WHAT THAT SOURCE IS WORTH, on the page the surgeon acts on. The citation names the
      // paper; it does not say the paper was ever assessed. Until 2026-09-05 those two claims
      // were carried by one line, so a small single-centre study read as authority in print.
      // `source` states n and provenance; `measurementCaveat` states the film-vs-CT mismatch
      // that no correction is applied for. Both are plain sentences, not engineering notes.
      const notes = [
        // Two different things, so two different labels. Calling the measurement caveat a "norm
        // source" read as nonsense on the first rendered page — caught by reading the PDF.
        cogs.set.source && `Norm source: ${cogs.set.source}`,
        cogs.set.measurementCaveat && `Measurement note: ${cogs.set.measurementCaveat}`,
        // A reference set produces no verdicts at all, so the verdict column comes back empty.
        // Empty must never read as "all normal" — the same rule the planning panel follows.
        cogs.set.role === 'reference'
          && 'This set is shown for reference only: no normal/abnormal judgement is made from it, '
             + 'so an empty verdict means not judged, not normal.',
      ].filter(Boolean)
      for (const note of notes) {
        for (const ln of wrap(S(note), band.w, size - 1, 'normal', measureText)) {
          text(band.x, ln, { size: size - 1, color: GREY })
          y += lineH * 0.75
        }
      }
    }
    if (afterRefusedKeys.size) {
      text(band.x, `Could not be recomputed on the planned anatomy: `
        + `${(cogs.afterRefused ?? []).map((r) => r.label).join('; ')}.`,
        { size: size * 0.75, color: GREY })
      y += lineH * 0.8
    }
    if ((cogs.lockedCount ?? 0) > 0 || (cogs.refusedCount ?? 0) > 0) {
      text(band.x, `Not shown: ${cogs.lockedCount ?? 0} measurement(s) awaiting landmarks, `
        + `${cogs.refusedCount ?? 0} refused - this table is not the complete analysis.`,
        { size: size - 1, color: GREY, weight: 'bold' })
      y += lineH
    }
    y += lineH * 0.4
  }

  // ── the frontal comparative rows, when the case has them ───────────────────
  const f = c.frontal
  if (f?.measured?.length) {
    text(band.x, 'Frontal (PA) - comparative', { size: size + 1.5, weight: 'bold' })
    y += lineH * 1.3
    for (const m of f.measured) {
      // The reading sentence IS the row: this picture reads in PA convention,
      // and a bare signed number is how a cant gets corrected on the wrong side.
      const line = `${m.label}: ${m.value} ${m.unit === 'deg' ? 'deg' : m.unit} - ${m.reading}`
      for (const ln of wrap(S(line), band.w, size, 'normal', measureText)) {
        text(band.x, ln)
        y += lineH * 0.85
      }
    }
    const frontalMissing = [...(f.locked ?? []), ...(f.refused ?? [])]
    if (frontalMissing.length) {
      // Locked AND refused — the producer emits both "so a short table cannot read as a complete
      // one", and only locked was printed (re-review finding).
      text(band.x, `Not measured: ${frontalMissing.map((l) => l.label).join('; ')}.`,
           { size: size - 1, color: GREY })
      y += lineH
    }
    for (const ln of wrap(S(f.normsNote ?? ''), band.w, size - 1, 'normal', measureText)) {
      text(band.x, ln, { size: size - 1, color: GREY })
      y += lineH * 0.72
    }
    y += lineH * 0.25
  }

  // ── provenance, always ─────────────────────────────────────────────────────
  const prov = []
  if (c.landmarks?.note) {
    prov.push(c.landmarks.confirmedBy
      ? `${c.landmarks.note} Confirmed by: ${c.landmarks.confirmedBy}.`
      : c.landmarks.note)
  }
  if (c.normsNote) prov.push(c.normsNote)
  for (const blockText of prov) {
    for (const ln of wrap(S(blockText), band.w, size - 1.5, 'normal', measureText)) {
      text(band.x, ln, { size: size - 1.5, color: GREY })
      y += lineH * 0.66
    }
    y += lineH * 0.15
  }

  // 🔴 Overflow is VISIBLE, never an overprinted footer. Same philosophy as the
  // unknown-block fallback: a broken page must look broken.
  if (y > band.bottom) {
    ops.push({
      op: 'text', x: g.page.w / 2, y: band.bottom,
      text: '[cephalometry content exceeds the page]',
      size: 9, weight: 'bold', align: 'center', color: GREY,
    })
  }
  return ops
}


/** Two columns, one scene each, the two different: the native / post-plan comparison. */
/**
 * Every cell naming the SAME one scene, more than one of them — a six-view page, a
 * row of views. Those are the pages whose renders share a camera scale, so the
 * shared crop applies; see SHARED_CROP in schema.js.
 */
function isOneSceneGrid(page) {
  const cells = page.cells ?? []
  if (cells.length < 2 || cells.some((c) => !c?.sceneId)) return false
  return cells.every((c) => c.sceneId === cells[0].sceneId)
}

function isComparisonShape(page) {
  if ((page.cols ?? 1) !== 2 || (page.rows ?? 1) < 2) return false
  const cells = page.cells ?? []
  if (cells.length !== page.rows * 2 || cells.some((c) => !c?.sceneId)) return false
  const left = cells.filter((_, i) => i % 2 === 0).map((c) => c.sceneId)
  const right = cells.filter((_, i) => i % 2 === 1).map((c) => c.sceneId)
  return left.every((s) => s === left[0]) && right.every((s) => s === right[0]) && left[0] !== right[0]
}

/**
 * The R×C image grid — 13 of the 24 orthognathic pages and most of the fibula
 * report. `frame: 'boxed'` draws the outer box and dividers (orthognathic);
 * `'open'` floats the images (fibula).
 */
function gridOps(page, g, band) {
  const ops = []
  const frame = page.frame ?? g.frame
  const rows = Math.max(1, page.rows ?? 1)
  const cols = Math.max(1, page.cols ?? 1)
  const h = band.bottom - band.top
  const badgeMm = ptToMm(g.viewBadge.size)
  // Cell boundaries. A template that STATES its grid (the master's `Grid - Native Skull`) and
  // a page of that grid's shape, on the unshifted band, take the stated rules — recorded as
  // rule centres and drawn ON them: a PDF stroke is centred on its path (G3, 29 Sep — the old
  // half-stroke subtraction put every line half a stroke off the master). Otherwise the band
  // is divided evenly.
  // The comparison page asks for the shared crop (`tightGroup`); a comparison page SAVED before
  // that field existed is recognised by its shape — two columns, each one scene down its
  // column, the two columns different — so a stored case fills its cells like a fresh draft.
  // 🔴 A page whose cells carry their OWN crop never joins the shared group. The
  // writer skips a member that already has one (cropTightGroups), so the rest would
  // grow to the union while those kept their zoom and the page would end at two
  // scales — exactly what the shared crop exists to prevent. The orbital design
  // confirmation's page 4 zooms its LL and RL cells to the orbit.
  //
  // `isOneSceneGrid` is the same back-compat sniff `isComparisonShape` is: a page
  // SAVED before the field existed still fills its cells like a fresh draft.
  const anyOwnCrop = (page.cells ?? []).some((c) => c?.crop)
  const tightGroup = !anyOwnCrop
    && (page.tightGroup ?? (isComparisonShape(page) || isOneSceneGrid(page)))
  const stated = g.frames?.grid
  const onFrame = stated && rows === stated.rows.length - 1 && cols === stated.cols.length - 1
    && Math.abs(band.top - g.content.top) < 0.01 && Math.abs(band.bottom - g.content.bottom) < 0.01
  const ys = onFrame ? [...stated.rows] : Array.from({ length: rows + 1 }, (_, r) => band.top + r * (h / rows))
  const xs = onFrame ? [...stated.cols] : Array.from({ length: cols + 1 }, (_, c) => band.x + c * (band.w / cols))
  for (let i = 0; i < rows * cols; i++) {
    const cell = page.cells?.[i]
    const r = Math.floor(i / cols), c = i % cols
    const x = xs[c], y = ys[r]
    const cellW = xs[c + 1] - xs[c], cellH = ys[r + 1] - ys[r]
    if (cell && (cell.sceneId || cell.override)) {
      ops.push({
        op: 'image', x, y, w: cellW, h: cellH, fit: 'contain',
        // `tightGroup`: the writer crops every image of this page to the union of their ink,
        // one rectangle for all, so the cells fill without losing the shared scale.
        ...(tightGroup ? { tightGroup: page.id } : {}),
        source: cell.override
          ? { kind: 'asset', assetId: cell.override }
          : { kind: 'scene', sceneId: cell.sceneId, view: cell.view },
        // A 2D crop of the RENDER, in fractions of it: {x,y,w,h}. The orbital
        // report's page 4 shows its left and right views zoomed to the orbit,
        // and ADR 0009 chose a crop over a second camera — adding view codes to
        // VIEW_DEFS would change default output for every other consumer of the
        // renderer. Layout only carries the rectangle; the writer does the
        // cropping, so this stays a pure display list.
        ...(cell.crop ? { crop: cell.crop } : {}),
      })
    }
    if (cell?.view && page.badge === 'perCell') {
      ops.push({
        op: 'text', x: x + cellW - g.viewBadge.inset, y: y + badgeMm + g.viewBadge.inset,
        text: cell.view, size: g.viewBadge.size, weight: 'normal', align: 'right', color: INK,
        title: VIEW_LABELS[cell.view],
      })
    }
    ops.push(...annotationOps(cell?.annotations ?? [], g, { x, y, w: cellW, h: cellH }))
  }

  if (page.badge === 'perColumn') {
    for (let c = 0; c < cols; c++) {
      const view = page.cells?.[c]?.view
      if (!view) continue
      ops.push({
        op: 'text', x: (xs[c] + xs[c + 1]) / 2, y: band.top + badgeMm,
        text: view, size: g.viewBadge.size, weight: 'normal', align: 'center', color: INK,
        title: VIEW_LABELS[view],
      })
    }
  }



  // The frame goes on LAST. A contain-fitted render routinely fills its cell on
  // one axis, so dividers emitted before the images were painted over — the
  // reference reports show the grid sitting on top of the renders, not under.
  if (frame === 'boxed') {
    const w = g.gridRule.width
    ops.push({
      op: 'rect', x: band.x, y: band.top, w: band.w, h,
      stroke: RULE_COLOR, lineWidth: w,
    })
    // `dividers: false` — the outer box only. The template's four-view leg page
    // puts its four badges inside ONE frame with no rules between them, and a
    // supplied reference is the specification.
    if (page.dividers === false) return ops
    for (let r = 1; r < rows; r++) {
      const y = ys[r]
      ops.push({ op: 'line', x1: band.x, y1: y, x2: band.x + band.w, y2: y, color: RULE_COLOR, width: w })
    }
    for (let c = 1; c < cols; c++) {
      const x = xs[c]
      ops.push({ op: 'line', x1: x, y1: band.top, x2: x, y2: band.top + h, color: RULE_COLOR, width: w })
    }
  }
  return ops
}

/** Prose pages: the Agreement and the Terms & Conditions. */
/**
 * How tall is one body node, and what lines does it break into?
 *
 * Used by BOTH the drawer below and the paginator (`flowTextPages`). They must
 * never be two implementations of "how tall is this" — a measurer that disagrees
 * with the drawer by one line is how text ends up half a line over a footer.
 */
function nodeMetrics(node, band, measureText, styles = null) {
  // Per-page overrides, because the delivered report does NOT set all its prose
  // the same way. Measured off the reference at 200dpi by row projection: the
  // Terms run at a 1.153 leading factor and the Agreement at 1.92, either side
  // of the 1.35 default. One global value made the Terms overflow onto two extra
  // pages, two of them almost blank.
  const base = TEXT_STYLES[node.kind] ?? TEXT_STYLES.p
  const style = styles?.[node.kind] ? { ...base, ...styles[node.kind] } : base
  const lineH = ptToMm(style.size) * style.leading
  if (node.kind === 'signature') {
    // Rule + label, then the block's own trailing space.
    const h = 2 + ptToMm(style.size) * 1.3
    return { style, lineH, lines: [], height: style.spaceBefore + h + style.spaceAfter }
  }
  const lines = node.kind === 'bullets'
    ? (node.items ?? []).flatMap((item) =>
      wrap(`- ${S(item)}`, band.w - 4, style.size, style.weight, measureText))
    : wrap(S(node.text), band.w, style.size, style.weight, measureText)
  return { style, lineH, lines, height: style.spaceBefore + lines.length * lineH + style.spaceAfter }
}

/** Where a text page's body starts — one number, used by drawer and paginator. */
const textTop = (band) => band.top + 4

/**
 * Split a text body into chunks that each fit the band.
 *
 * `textOps` walks a y cursor down the page with no bound of its own, so without
 * this a body longer than the band prints straight through the footer and off
 * the sheet — measured at 287.8 mm against a footer rule at 269.4 mm on the
 * Terms and Conditions page. That is the same defect the STL/OBJ report shipped
 * with its waiver notes, so it is fixed here the same way: measure, then flow.
 *
 * A single node taller than a whole band (one enormous paragraph) still gets its
 * own chunk rather than an infinite loop — it will overflow, and the guard test
 * says so, which is the honest outcome for input no page can hold.
 */
export function splitBody(body, band, measureText, styles = null) {
  const nodes = body ?? []
  if (!nodes.length) return []
  const capacity = band.bottom - textTop(band)
  const heights = nodes.map((n) => nodeMetrics(n, band, measureText, styles).height)

  // How many pages does this body genuinely need? Greedy fill answers that.
  let pages = 1
  let used = 0
  for (const h of heights) {
    if (used && used + h > capacity) { pages += 1; used = 0 }
    used += h
  }
  if (pages === 1) return [nodes]

  // Now spread the content EVENLY over that many pages instead of packing each
  // page full and leaving the remainder on the last one. Greedy packing put
  // three lines — 9% of a page — alone on page 9 of a Terms and Conditions,
  // reached simply by giving the report a real hospital name: the wrapped
  // header shrinks the band, and the tail spills. A near-blank page in the
  // middle of a legal document reads as a fault to whoever receives it.
  const total = heights.reduce((a, b) => a + b, 0)
  const target = total / pages
  const chunks = []
  let current = []
  let y = 0
  let remaining = total
  let left = pages
  for (const [i, node] of nodes.entries()) {
    const h = heights[i]
    const overflows = current.length > 0 && y + h > capacity
    // Break early for balance only when the rest genuinely still fits in the
    // pages that are left — otherwise balancing would ADD a page.
    const balanced = current.length > 0 && left > 1 && y >= target
      && remaining <= (left - 1) * capacity
    if (overflows || balanced) {
      chunks.push(current)
      current = []
      y = 0
      left -= 1
    }
    current.push(node)
    y += h
    remaining -= h
  }
  if (current.length) chunks.push(current)
  return chunks
}

function textOps(page, g, band, measureText) {
  const styles = page.styles ?? null
  const ops = []
  let y = textTop(band)

  for (const node of page.body ?? []) {
    const { style, lineH, lines } = nodeMetrics(node, band, measureText, styles)
    y += style.spaceBefore

    if (node.kind === 'signature') {
      // Two rules with labels under them — "Surgeon Signature" / "Date".
      const w = band.w * 0.38
      const gap = band.w - 2 * w
      const ys = y + 2
      for (const [i, label] of (node.labels ?? ['Surgeon Signature', 'Date']).entries()) {
        const x = band.x + i * (w + gap)
        ops.push({ op: 'line', x1: x, y1: ys, x2: x + w, y2: ys, color: RULE_COLOR, width: 0.5 })
        ops.push({
          op: 'text', x: x + w / 2, y: ys + ptToMm(style.size) * 1.3, text: label,
          size: style.size, weight: 'normal', align: 'center', color: INK,
        })
      }
      y = ys + ptToMm(style.size) * 1.3 + style.spaceAfter
      continue
    }

    for (const line of lines) {
      ops.push({
        op: 'text',
        x: style.align === 'center' ? g.page.w / 2 : band.x,
        y, text: line, size: style.size, weight: style.weight,
        align: style.align, color: INK,
      })
      y += lineH
    }
    y += style.spaceAfter
  }
  return ops
}

const TEXT_STYLES = {
  h1: { size: 13, weight: 'bold', align: 'center', leading: 1.35, spaceBefore: 3, spaceAfter: 3 },
  h2: { size: 10, weight: 'bold', align: 'left', leading: 1.3, spaceBefore: 2.5, spaceAfter: 1 },
  p: { size: 10, weight: 'normal', align: 'left', leading: 1.35, spaceBefore: 0, spaceAfter: 2 },
  bullets: { size: 10, weight: 'normal', align: 'left', leading: 1.35, spaceBefore: 0, spaceAfter: 2 },
  signature: { size: 12, weight: 'normal', align: 'left', leading: 1.3, spaceBefore: 8, spaceAfter: 4 },
}


/**
 * `info` — the Source data information page (reference p2).
 *
 * Left column: titled groups of label/value rows — "Skull data" with the CT
 * fields, "Intraoral scan" with the IOS fields. Right column and the space
 * below: renders of what that data produced.
 *
 * Values wrap inside the column. The reference never wrapped because its demo
 * case had short values; real ones ("Faculty of Dental Science, M S Ramaiah…")
 * do, and a value running under the image is the same overprint defect the SSE
 * report shipped with.
 */
/**
 * The report date as the master's placeholder spells it: `YYYY-MMM-DD` -> `2026-Oct-05`.
 *
 * SPLIT, never parsed. `new Date('2026-10-05')` is UTC midnight, so formatting through a Date
 * prints the PREVIOUS day in every timezone west of Greenwich. The input is the Builder's own
 * <input type="date">, which is always `YYYY-MM-DD`; anything else is handed back untouched
 * rather than guessed at, so a hand-set value still prints what was typed.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const fmtReportDate = (iso) => {
  const t = String(iso ?? '').trim()
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t)
  return m && +m[2] >= 1 && +m[2] <= 12 ? `${m[1]}-${MONTHS[+m[2] - 1]}-${m[3]}` : t
}

/**
 * The source-data page as the MASTER lays it out (template.js `sourceData`, the hidden
 * `Singe View` variant): a left-aligned heading, TWO text columns of label/value rows, the
 * quality waiver at its own place, and the report date on the caption line.
 *
 * A render goes in the right column — see the slot below for why it was missing.
 *
 * Groups go left, right, then down the left column: the master's left column reads "Skull
 * data" then "Leg data", its right "Intraoral scan/ Denture scan" then "Reconstruction plate
 * scan". The typed case summary follows the left column's last group; the waiver sits at the
 * master's baseline unless the column has already run past it.
 */
function sourceDataOps(page, g, band, measureText, doc = null) {
  const ops = []
  const sd = g.sourceData
  const size = sd.size
  const lineH = sd.lineH
  const c = page.captions ?? {}

  const heading = S(c.tc || page.title || 'CT/ Source data information')
  if (heading) {
    ops.push({
      op: 'text', x: sd.heading.x, y: sd.heading.baseline, text: heading,
      size: sd.heading.size, weight: 'normal', align: 'left', color: INK,
    })
  }

  const cols = sd.columns.map((col) => ({ ...col, y: col.baseline, w: col.right - col.x }))
  const groups = page.groups ?? []
  const place = (gi) => (gi === 1 ? cols[1] : cols[0])
  const rowsOf = (col, group) => {
    if (group.title) {
      ops.push({ op: 'text', x: col.x, y: col.y, text: S(group.title), size, weight: 'bold', align: 'left', color: INK })
      col.y += lineH
    }
    for (const row of group.rows ?? []) {
      const [label, value, style] = row
      const head = `${S(label)}: `
      const headW = measureText(head, size, 'normal')
      ops.push({ op: 'text', x: col.x, y: col.y, text: head, size, weight: 'normal', align: 'left', color: INK })
      const vWeight = style?.bold ? 'bold' : 'normal'
      const lines = wrap(S(value ?? ''), Math.max(col.w - headW, 12), size, vWeight, measureText)
      for (const [i, ln] of lines.entries()) {
        ops.push({
          op: 'text', x: col.x + headW, y: col.y + i * lineH, text: ln,
          size, weight: vWeight, align: 'left', color: style?.color ?? INK,
        })
      }
      col.y += Math.max(1, lines.length) * lineH
    }
    col.y += lineH
  }
  // A group with a title and no rows labelled a picture on the old layout; there is no
  // picture here, so it is not printed as a bare heading.
  for (const [gi, group] of groups.entries()) if ((group.rows ?? []).length) rowsOf(place(gi), group)

  const left = cols[0]
  if (page.summary) {
    ops.push({ op: 'text', x: left.x, y: left.y, text: 'Case summary', size, weight: 'bold', align: 'left', color: INK })
    left.y += lineH
    for (const ln of wrap(S(page.summary), left.w, size, 'normal', measureText)) {
      ops.push({ op: 'text', x: left.x, y: left.y, text: ln, size, weight: 'normal', align: 'left', color: INK })
      left.y += lineH
    }
    left.y += lineH
  }

  const wv = page.waiverBlock
  if (wv) {
    let y = Math.max(sd.waiver.baseline, left.y)
    const head = `${S(wv.label ?? 'Quality waiver')}: `
    const headW = measureText(head, size, 'normal')
    ops.push({ op: 'text', x: sd.waiver.x, y, text: head, size, weight: 'normal', align: 'left', color: INK })
    ops.push({
      op: 'text', x: sd.waiver.x + headW, y, text: S(wv.value ?? ''),
      size, weight: wv.valueStyle?.bold ? 'bold' : 'normal', align: 'left', color: wv.valueStyle?.color ?? INK,
    })
    y += lineH
    if (wv.notes) {
      ops.push({ op: 'text', x: sd.waiver.x, y, text: S(wv.notesLabel ?? 'Notes:'), size, weight: 'normal', align: 'left', color: INK })
      y += lineH
      // The notes are unbounded free text and this block has a floor: the footer
      // rule. Measured on the master before this clamp — 500 characters ran past
      // the footer and 700 ran off the sheet. The STL/OBJ report shipped that
      // defect once and now measures-then-paginates for it; a source-data page has
      // nowhere to flow to, so it fills what it has and says the rest was cut.
      const floor = ruleY(g.ruleFooter) - lineH
      const lines = wrap(S(wv.notes), left.w, size, 'normal', measureText)
      let drawn = 0
      for (const ln of lines) {
        if (y > floor) break
        ops.push({ op: 'text', x: sd.waiver.x, y, text: ln, size, weight: 'normal', align: 'left', color: INK })
        y += lineH
        drawn += 1
      }
      if (drawn < lines.length) {
        ops.push({
          op: 'text', x: sd.waiver.x, y: Math.min(y, floor + lineH), size, weight: 'normal',
          align: 'left', color: INK,
          text: `… ${lines.length - drawn} more line${lines.length - drawn === 1 ? '' : 's'} of notes did not fit on this page.`,
        })
      }
    }
  }

  // The render slot — the MASTER's own right column. Its source-data page fills that column
  // with "Intraoral scan/ Denture scan" text, so a picture takes what the column has left under
  // the text, and the whole column when the format puts no text there. The CT-data page is the
  // latter: one group, left column.
  //
  // This slot was LOST on 26 Sep 2026 and nothing caught it. Until then every format carried its
  // own geometry and none stated `sourceData`, so an info page went through the generic infoOps
  // below and its renders were drawn. Pointing every format at the chassis (formats.js
  // geometryFor, commit dd12cd8c) routed them HERE instead, and this function had no render
  // slot — so the orbital report's page-1 isometric skull and the page-organised path's two
  // source renders were dropped in silence. Measured 5 Oct 2026: zero image ops on a page
  // carrying one, in all four formats, and one op the moment `sourceData` is absent. Team, same
  // day: "There is no option to add OBJ files for generating images on the Preview Image page."
  const imgs = page.images ?? []
  if (imgs.length) {
    const col = cols[1]
    // One rule for both cases, and it is right in both because `rowsOf` ends with
    // `col.y += lineH`. An UNTOUCHED column: 76.20 - 5.42 = 70.78, the top of the text block
    // where its first line would have started. A column that HAS text: the cursor is already a
    // full line past the last baseline, so subtracting one leaves exactly one clear line under
    // it (measured on the two-column fixture: last baseline 81.62, picture top 87.04).
    const top = col.y - lineH
    const room = band.bottom - top
    // Under two lines of height there is no picture, only a sliver. The master leaves 64.7 mm
    // here; a format that ran its right column further down leaves less.
    if (room >= lineH * 2) {
      // A group with a title and NO rows is a label for a picture, not a block of metadata —
      // the page-organised path emits exactly that. Paired by index, so each heading sits above
      // the render it names. Groups with rows have already gone into their columns.
      const labels = groups.map((gr) => ((gr.rows ?? []).length ? '' : S(gr.title ?? '')))
      const each = room / imgs.length
      for (const [i, source] of imgs.entries()) {
        let y = top + i * each
        let h = each
        if (labels[i]) {
          ops.push({
            op: 'text', x: col.x, y: y + lineH * 0.8, text: labels[i],
            size, weight: 'bold', align: 'left', color: INK,
          })
          y += lineH
          h -= lineH
        }
        ops.push({ op: 'image', x: col.x, y, w: col.w, h, fit: 'contain', source })
      }
    }
  }

  // The report date, at the master's own slot: right-justified to the content frame's right
  // rule, baseline 273.65. Team, 5 Oct 2026: "Include the date on which the report was generated
  // in the images"; Deepak: "only on page 1 is the ask" — which this satisfies structurally,
  // because only the source-data page is drawn by this function.
  //
  // The master's case-ID layer on the SAME line stays unprinted: the team's test of 28 Sep asked
  // for it to go, and the identity block carries the case ID on every page.
  const reportDate = fmtReportDate(doc?.meta?.dateStr)
  if (sd.date && reportDate) {
    ops.push({
      op: 'text', x: sd.date.right, y: sd.date.baseline, text: reportDate,
      size: sd.date.size, weight: 'normal', align: 'right', color: INK,
    })
  }
  return ops
}


/**
 * The in-band heading rule the info and ceph blocks share — ONE place (review finding: cephOps
 * re-implemented it and even cited "the infoOps rule" in a comment; two copies of a contract is
 * how an override typed in PageEditor's Top-centre box silently does nothing on one block).
 *
 * The contract: `captions.tc` WINS over `title`, the heading draws INSIDE the content box
 * (captionOps deliberately does not draw tc for these blocks), heading size is body + 3.5.
 * Returns the new cursor; emits nothing for an empty heading.
 */
function bandHeading(ops, page, { x, y, size, fallback = '', advance }) {
  const heading = S(page.captions?.tc || page.title || fallback)
  if (!heading) return y
  ops.push({ op: 'text', x, y, text: heading, size: size + 3.5, weight: 'normal', align: 'left', color: INK })
  return y + advance
}

function infoOps(page, g, band, measureText, doc = null) {
  if (g.sourceData) return sourceDataOps(page, g, band, measureText, doc)
  const ops = []
  const colW = band.w * 0.46
  const labelSize = g.info?.size ?? 10.5
  const lineH = ptToMm(labelSize) * 1.32
  let y = band.top + lineH
  // The page heading lives INSIDE the content box, left-aligned, as on the
  // reference. Routing it through the caption ring instead drew it on top of
  // the frame rule.
  //
  // `captions.tc` wins over `title` because the Top-centre box in PageEditor is
  // where an operator edits a heading, and captionOps deliberately does not draw
  // it for this block — so this is the one place it can appear. Without this an
  // override typed against the old grid page silently did nothing.
  y = bandHeading(ops, page, { x: band.x, y, size: labelSize, advance: lineH * 1.9 })

  // A group with a TITLE and NO ROWS is a label for a picture, not a block of
  // metadata. The page-organised path emits exactly that — no CT fetch and no
  // typed intraoral fields reach it — and routing those titles through the text
  // column stacked "Skull data" and "Intraoral scan" together at the top left
  // while their renders sat right and below, so neither heading named the thing
  // beside it. Pair them with their images instead. With rows present nothing
  // changes and the reference layout stands.
  const groupsAll = page.groups ?? []
  const imgsAll = page.images ?? []
  // Only pair when every group HAS an image to label. Past that the titles have
  // nowhere to go and were dropped in silence — latent while both producers keep
  // groups and images in lockstep, but a silent drop in a generic renderer is
  // the shape of defect this file keeps being bitten by.
  const labelPairing = imgsAll.length > 0
    && groupsAll.length > 0
    && groupsAll.length <= imgsAll.length
    && groupsAll.every((gr) => !(gr.rows ?? []).length)
  const textGroups = labelPairing ? [] : groupsAll

  for (const group of textGroups) {
    if (group.title) {
      ops.push({
        op: 'text', x: band.x, y, text: S(group.title),
        size: labelSize + 1.5, weight: 'bold', align: 'left', color: INK,
      })
      y += lineH * 1.25
    }
    for (const row of group.rows ?? []) {
      // A row is [label, value] or [label, value, style]. `style` carries
      // { color, bold } and applies to the VALUE only — the label stays plain.
      // The orbital report prints the study date in red when the scan is more
      // than 30 days older than the DICOM receipt, which is the same rule
      // sseReport.js already uses. Deciding that is the recipe's job; drawing it
      // is this line.
      const [label, value, style] = row
      const head = `${S(label)}: `
      const headW = measureText(head, labelSize, 'normal')
      ops.push({ op: 'text', x: band.x, y, text: head, size: labelSize, weight: 'normal', align: 'left', color: INK })
      const vWeight = style?.bold ? 'bold' : 'normal'
      const lines = wrap(S(value ?? ''), Math.max(colW - headW, 12), labelSize, vWeight, measureText)
      for (const [i, ln] of lines.entries()) {
        ops.push({
          op: 'text', x: band.x + headW, y: y + i * lineH, text: ln,
          size: labelSize, weight: vWeight, align: 'left',
          color: style?.color ?? INK,
        })
      }
      y += Math.max(1, lines.length) * lineH
    }
    y += lineH * 0.6
  }

  // The quality waiver: a label, a value drawn in its own style ("Yes" in red on
  // the reference), then the operator's notes wrapped under it. Sits in the text
  // column under the groups, so a long note pushes nothing off the page — it
  // simply runs down the column it owns.
  const wv = page.waiverBlock
  if (wv) {
    const head = `${S(wv.label ?? 'Quality waiver')}: `
    const headW = measureText(head, labelSize, 'normal')
    ops.push({ op: 'text', x: band.x, y, text: head, size: labelSize, weight: 'normal', align: 'left', color: INK })
    ops.push({
      op: 'text', x: band.x + headW, y, text: S(wv.value ?? ''),
      size: labelSize, weight: wv.valueStyle?.bold ? 'bold' : 'normal', align: 'left',
      color: wv.valueStyle?.color ?? INK,
    })
    y += lineH * 1.15
    if (wv.notes) {
      ops.push({
        op: 'text', x: band.x, y, text: S(wv.notesLabel ?? 'Notes:'),
        size: labelSize, weight: 'normal', align: 'left', color: INK,
      })
      y += lineH
      for (const ln of wrap(S(wv.notes), colW, labelSize, 'normal', measureText)) {
        ops.push({
          op: 'text', x: band.x, y, text: ln,
          size: labelSize, weight: 'normal', align: 'left', color: INK,
        })
        y += lineH
      }
    }
  }

  // The case summary — FIBULA p2: "The left fibula has been utilized, with the
  // first osteotomy commencing 8 cm from the lateral malleolus…". A typed
  // paragraph under the data lists, in the text column, so a long summary runs
  // down the column it owns and never pushes the render. It used to have no
  // field at all and the first house-template sample had to carry it as a
  // bottom caption, shrunk to one small line.
  if (page.summary) {
    y += lineH * 0.4
    ops.push({
      op: 'text', x: band.x, y, text: 'Case summary',
      size: labelSize + 1.5, weight: 'bold', align: 'left', color: INK,
    })
    y += lineH * 1.25
    for (const ln of wrap(S(page.summary), colW, labelSize, 'normal', measureText)) {
      ops.push({ op: 'text', x: band.x, y, text: ln, size: labelSize, weight: 'normal', align: 'left', color: INK })
      y += lineH
    }
    y += lineH * 0.6
  }

  // Images: the first fills the right column, the second the width below the
  // text. Both contain-fitted, so neither is stretched.
  const imgs = imgsAll
  // When the titles label the pictures, each one is drawn immediately above its
  // own image and the image band is pulled down to make room, so a heading can
  // never overlap the render it names.
  const capH = labelPairing ? lineH * 1.5 : 0
  const capFor = (i, x, yTop, w) => {
    const t = S(groupsAll[i]?.title ?? '')
    if (!labelPairing || !t) return
    ops.push({
      op: 'text', x, y: yTop + ptToMm(labelSize) * 0.9, text: t,
      size: labelSize + 1.5, weight: 'bold', align: 'left', color: INK,
    })
  }
  if (imgs[0]) {
    // With a second image below, the first takes the top half of the right
    // column; alone, it takes the whole height rather than leaving the lower
    // half of the page blank.
    // With titles paired to pictures there is no text column to sit beside, so
    // the first image takes the FULL width rather than hugging the right edge
    // of an empty half-page.
    const x0 = labelPairing ? band.x : band.x + colW + 4
    const w0 = labelPairing ? band.w : band.w - colW - 4
    // Start BELOW the page heading, not at the band top — `y` already carries
    // the heading's advance. Anchoring the first label to band.top drew
    // "Skull data" straight through "Source data information".
    const top0 = labelPairing ? y : band.top
    capFor(0, x0, top0, w0)
    ops.push({
      op: 'image', x: x0, y: top0 + capH, w: w0,
      h: (band.bottom - top0 - capH) * (imgs[1] ? 0.5 : 1), fit: 'contain', source: imgs[0],
    })
  }
  if (imgs[1]) {
    const top = labelPairing
      ? y + capH + (band.bottom - y - capH) * 0.5
      : Math.max(y + 4, band.top + (band.bottom - band.top) * 0.52)
    capFor(1, band.x, top, band.w)
    ops.push({
      op: 'image', x: band.x, y: top + capH, w: band.w, h: band.bottom - top - capH,
      fit: 'contain', source: imgs[1],
    })
  }
  if (page.frame ?? g.frame) {
    ops.push({
      op: 'rect', x: band.x - 2, y: band.top - 2, w: band.w + 4, h: band.bottom - band.top + 4,
      stroke: RULE_COLOR, lineWidth: g.gridRule.width,
    })
  }
  return ops
}

/**
 * `steps` — the planning narrative (reference pp. 5-6).
 *
 * A grid of panels, each a render with a numbered caption UNDER it:
 * "**Step 01**: BSSO was done on the mandible". The caption wraps and is
 * centred, and its height varies from one to four lines, so every panel in a
 * row reserves the tallest caption in that row rather than a fixed band —
 * otherwise the longest caption silently collides with the render beneath it.
 */
function stepsOps(page, g, band, measureText) {
  const ops = []
  const steps = page.steps ?? []
  if (!steps.length) return ops
  const cols = Math.max(1, page.cols ?? 2)
  const rows = Math.ceil(steps.length / cols)
  const cellW = band.w / cols
  const cellH = (band.bottom - band.top) / rows
  const size = g.stepCaption?.size ?? 11
  const lineH = ptToMm(size) * 1.3
  const badgeMm = ptToMm(g.viewBadge.size)

  // Wrap every caption first: a row's image height is the row height minus the
  // TALLEST caption in that row.
  const wrapped = steps.map((st) => {
    const label = st.label ?? ''
    const text = [label ? `${label}: ` : '', S(st.text ?? '')].join('')
    return wrap(text, cellW - 8, size, 'normal', measureText)
  })
  const rowCaptionH = Array.from({ length: rows }, (_, r) => {
    const inRow = wrapped.slice(r * cols, r * cols + cols)
    return Math.max(1, ...inRow.map((w) => w.length)) * lineH + 2
  })

  for (const [i, st] of steps.entries()) {
    const r = Math.floor(i / cols)
    const c = i % cols
    const x = band.x + c * cellW
    const yTop = band.top + r * cellH
    const imgH = cellH - rowCaptionH[r]
    if (st.sceneId || st.override) {
      ops.push({
        op: 'image', x, y: yTop, w: cellW, h: imgH, fit: 'contain',
        source: st.override ? { kind: 'asset', assetId: st.override }
          : { kind: 'scene', sceneId: st.sceneId, view: st.view },
      })
    }
    if (st.view) {
      ops.push({
        op: 'text', x: x + cellW - g.viewBadge.inset, y: yTop + badgeMm + g.viewBadge.inset,
        text: st.view, size: g.viewBadge.size, weight: 'normal', align: 'right', color: INK,
        title: VIEW_LABELS[st.view],
      })
    }
    ops.push(...annotationOps(st.annotations ?? [], g, { x, y: yTop, w: cellW, h: imgH }))
    // Caption: the "Step 01" label is bold, the rest normal, so the label is
    // drawn separately over the first line rather than faking bold with a font.
    let cy = yTop + imgH + lineH
    for (const [li, ln] of wrapped[i].entries()) {
      ops.push({
        op: 'text', x: x + cellW / 2, y: cy + li * lineH, text: ln,
        size, weight: 'normal', align: 'center', color: INK,
      })
    }
    if (st.label) {
      const first = wrapped[i][0] ?? ''
      const fullW = measureText(first, size, 'normal')
      ops.push({
        op: 'text', x: x + cellW / 2 - fullW / 2, y: cy, text: `${S(st.label)}:`,
        size, weight: 'bold', align: 'left', color: INK,
      })
    }
    cy += wrapped[i].length * lineH
  }
  // The master's single-view box (`Grid`: the outer frame, no dividers). ORTHO p5 — the steps
  // page — sits inside it; this block never drew one and the sample page was unboxed
  // (Deepak, 26 Sep). Last, over the renders, as gridOps draws its frame.
  if (page.frame ?? g.frame) {
    ops.push({
      op: 'rect', x: band.x, y: band.top, w: band.w, h: band.bottom - band.top,
      stroke: RULE_COLOR, lineWidth: g.gridRule.width,
    })
  }
  return ops
}

/**
 * `measure` — stacked measurement panels (reference pp. 7-8, 13).
 *
 * One render per row with a view badge and `dimension` annotations on it. The
 * "Units = mm" line and the explanatory sentence are sub-captions, which the
 * caption ring already draws and already subtracts from the band.
 */
function measureOps(page, g, band, measureText = null) {
  const ops = []
  const rowsIn = page.rows ?? []
  if (!rowsIn.length) return ops
  // The notes — ORTHO p8/p9: "Notes: The measurements are taken from the
  // infraorbital rim to the incisor tip…". A strip at the foot of the band, the
  // rows shrunk to make room, so the note is on the page that shows the thing
  // measured and never becomes a caption that overruns the frame.
  const noteSize = g.info?.size ?? 10.5
  const noteLineH = ptToMm(noteSize) * 1.32
  const noteLines = page.notes && measureText
    ? wrap(S(page.notes), band.w - 6, noteSize, 'normal', measureText) : []
  const noteH = noteLines.length ? (noteLines.length + 1.6) * noteLineH : 0
  const rowH = (band.bottom - band.top - noteH) / rowsIn.length
  const badgeMm = ptToMm(g.viewBadge.size)
  for (const [i, row] of rowsIn.entries()) {
    const y = band.top + i * rowH
    if (row.sceneId || row.override) {
      ops.push({
        op: 'image', x: band.x, y, w: band.w, h: rowH, fit: 'contain',
        source: row.override ? { kind: 'asset', assetId: row.override }
          : { kind: 'scene', sceneId: row.sceneId, view: row.view },
      })
    }
    if (row.view) {
      ops.push({
        op: 'text', x: band.x + band.w - g.viewBadge.inset, y: y + badgeMm + g.viewBadge.inset,
        text: row.view, size: g.viewBadge.size + 4, weight: 'normal', align: 'right', color: INK,
        title: VIEW_LABELS[row.view],
      })
    }
    ops.push(...annotationOps(row.annotations ?? [], g, { x: band.x, y, w: band.w, h: rowH }))
  }
  if (noteLines.length) {
    let y = band.bottom - noteH + noteLineH * 1.2
    ops.push({ op: 'text', x: band.x + 3, y, text: 'Notes:', size: noteSize, weight: 'bold', align: 'left', color: INK })
    y += noteLineH
    for (const ln of noteLines) {
      ops.push({ op: 'text', x: band.x + 3, y, text: ln, size: noteSize, weight: 'normal', align: 'left', color: INK })
      y += noteLineH
    }
  }
  if (page.frame ?? g.frame) {
    ops.push({
      op: 'rect', x: band.x, y: band.top, w: band.w, h: band.bottom - band.top,
      stroke: RULE_COLOR, lineWidth: g.gridRule.width,
    })
  }
  return ops
}

/**
 * `gallery` — the print-models pages (reference pp. 20-21).
 *
 * One to three renders stacked down the page, each with leader-line callouts
 * naming the parts, inside the template's frame.
 */
function galleryOps(page, g, band, measureText) {
  const ops = []
  const items = page.items ?? []
  if (!items.length) return ops
  const size = g.stepCaption?.size ?? 11
  const lineH = ptToMm(size) * 1.3
  // `cols` arranges the items side by side. One column is the stack this block
  // always drew. The delivered pages are not stacks: FIBULA p9 puts four leg
  // views across the page and ORTHO p15 puts five print models in two columns —
  // stacked, three spacers on the first house-template sample came out a third
  // of the height each, and the leg views were slivers.
  const cols = Math.max(1, Math.min(page.cols ?? 1, items.length))
  const rows = Math.ceil(items.length / cols)
  const gap = 3
  // A legend takes its own strip at the foot of the band and the pictures are
  // fitted above it. It used to be drawn at a fixed spot over whatever was
  // there — fine under the landscape ZBrush map it was measured against, and on
  // top of a portrait one.
  const legendH = legendHeight(page, g)
  const cellW = (band.w - gap * (cols - 1)) / cols
  const cellH = (band.bottom - legendH - band.top - gap * (rows - 1)) / rows
  for (const [i, it] of items.entries()) {
    const r = Math.floor(i / cols)
    const c = i % cols
    const x = band.x + c * (cellW + gap)
    const y = band.top + r * (cellH + gap)
    const capLines = it.caption ? wrap(S(it.caption), cellW - 8, size, 'normal', measureText) : []
    const imgH = cellH - capLines.length * lineH - (capLines.length ? 2 : 0)
    if (it.sceneId || it.override) {
      ops.push({
        // `tight`: the writer crops the render to its ink before the contain-fit. A gallery cell
        // is often far from the render's own shape — three leg views across the page came out
        // a third of the cell's height because each square-ish render was fitted by its width
        // (Deepak, 28 Sep: "too small, we are not using the space available on the page").
        op: 'image', x, y, w: cellW, h: imgH, fit: 'contain', tight: true,
        source: it.override ? { kind: 'asset', assetId: it.override }
          : { kind: 'scene', sceneId: it.sceneId, view: it.view },
      })
    }
    ops.push(...annotationOps(it.annotations ?? [], g, { x, y, w: cellW, h: imgH }))
    for (const [li, ln] of capLines.entries()) {
      ops.push({
        op: 'text', x: x + cellW / 2, y: y + imgH + (li + 1) * lineH,
        text: ln, size, weight: 'normal', align: 'center', color: INK,
      })
    }
  }
  ops.push(...legendOps(page, g, band, measureText))
  // The master's single-view box. This block used to draw none ("photographs of
  // deliverables, not an ortho set") — but ORTHO p15, FIBULA p9 and p20 and both colour-map
  // pages all sit inside the box, and the sample pages 8, 9 and 10 came out unboxed
  // (Deepak, 26 Sep). The template's frame style decides, as on every other block.
  if (page.frame ?? g.frame) {
    ops.push({
      op: 'rect', x: band.x, y: band.top, w: band.w, h: band.bottom - band.top,
      stroke: RULE_COLOR, lineWidth: g.gridRule.width,
    })
  }
  return ops
}

/**
 * A colour key: a filled swatch, a label, and the value it stands for.
 *
 * The orbital report's volume page carries three — "Native right orbit:
 * 29.24cc" and so on — and every one of those numbers is COMPUTED from the mesh
 * (volumes.js), never typed. Geometry measured off the reference at 600 dpi:
 * 2.8 mm square swatches on a 7.1 mm pitch at x 124.3 mm, labels left-aligned at
 * 129.78 mm, bottom-right of the content band.
 *
 * Anchored to the band's BOTTOM so it sits under the artwork however many items
 * the page has, and the label/value pair is one left-aligned run rather than a
 * right-aligned value, because that is how the reference sets it.
 */
/** The height a page's legend takes at the foot of the band, 0 without one. */
function legendHeight(page, g) {
  const n = (page.legend ?? []).length
  if (!n) return 0
  const pitch = g.legend?.pitch ?? 7.1
  return 4.0 + (n - 1) * pitch + ptToMm(g.legend?.size ?? 13.0) + 2
}

function legendOps(page, g, band, measureText) {
  const rows = page.legend ?? []
  if (!rows.length) return []
  const ops = []
  const size = g.legend?.size ?? 13.0
  const pitch = g.legend?.pitch ?? 7.1
  const sw = g.legend?.swatch ?? 2.8
  const textGap = g.legend?.textGap ?? 2.5
  // The reference's block ends 4 mm above the content box's lower rule.
  const lastBaseline = band.bottom - 4.0
  const firstBaseline = lastBaseline - (rows.length - 1) * pitch
  // Left edge: measured at 124.33 mm on a 195 mm content width. Expressed as an
  // offset from the band so a different content width keeps the block on the
  // page rather than off it.
  const swX = band.x + (g.legend?.xFrac ?? 0.6017) * band.w
  for (const [i, row] of rows.entries()) {
    const baseline = firstBaseline + i * pitch
    ops.push({
      op: 'rect', x: swX, y: baseline - sw, w: sw, h: sw,
      fill: row.color, stroke: row.color, lineWidth: 0.1,
    })
    const text = S(row.value ? `${row.label}: ${row.value}` : row.label)
    ops.push({
      op: 'text', x: swX + sw + textGap, y: baseline, text,
      size, weight: 'normal', align: 'left', color: INK,
      maxWidth: Math.max(20, band.x + band.w - (swX + sw + textGap)),
    })
  }
  return ops
}

// ── annotations ──────────────────────────────────────────────────────────────

/**
 * Overlays, in normalised coordinates. `frame` is the rect they are relative to
 * — a cell for cell annotations, the whole page for page annotations — which is
 * what lets an arrow stay put when a page is reordered or a cell is re-rendered.
 */
/**
 * A solid arrowhead at (x2,y2) pointing away from (x1,y1), as two short strokes
 * rather than a filled polygon — the display list has no polygon op, and at
 * 2-3mm the difference is invisible while the op set stays small.
 */
function arrowHead(x1, y1, x2, y2, len, color) {
  const dx = x2 - x1
  const dy = y2 - y1
  const m = Math.hypot(dx, dy)
  if (!m) return []
  const ux = dx / m
  const uy = dy / m
  const spread = 0.42
  return [-1, 1].map((sign) => ({
    op: 'line',
    x1: x2,
    y1: y2,
    x2: x2 - len * (ux * Math.cos(spread) - sign * uy * Math.sin(spread)),
    y2: y2 - len * (uy * Math.cos(spread) + sign * ux * Math.sin(spread)),
    color,
    width: 0.5,
  }))
}

function annotationOps(annotations, g, frame = null) {
  const box = frame ?? { x: 0, y: 0, w: g.page.w, h: g.page.h }
  const X = (u) => box.x + u * box.w
  const Y = (v) => box.y + v * box.h
  const ops = []

  for (const a of annotations) {
    switch (a.kind) {
      case 'text':
        ops.push({
          op: 'text', x: X(a.x), y: Y(a.y), text: a.text ?? '',
          size: a.size ?? 10, weight: a.weight ?? 'normal',
          align: a.align ?? 'left', color: a.color ?? INK, halo: a.halo ?? true,
        })
        break
      case 'line':
      case 'leader':
        ops.push({
          op: 'line', x1: X(a.x1), y1: Y(a.y1), x2: X(a.x2), y2: Y(a.y2),
          color: a.color ?? INK, width: a.width ?? 0.4,
        })
        break
      // `r` is a TRUE circle: one radius, taken against the frame width and used
      // for both axes. rx/ry stay available for a deliberate ellipse.
      //
      // Without `r` there is no way to ask for a circle: rx and ry normalise
      // against the frame's width and HEIGHT, which on a full A4 page differ by
      // 1.41, so rx === ry drew a visibly tall oval. "Point of rotation" on the
      // reference is a circle.
      case 'circle': {
        const rx = a.r != null ? a.r * box.w : a.rx * box.w
        const ry = a.r != null ? a.r * box.w : a.ry * box.h
        ops.push({
          op: 'ellipse', cx: X(a.cx), cy: Y(a.cy), rx, ry,
          stroke: a.color ?? INK, lineWidth: a.width ?? 0.4,
        })
        break
      }
      // An arrow is a line plus a solid head, and the reference uses it to
      // carry a measurement ("3.85mm" over a left-pointing arrow, p5).
      case 'arrow': {
        const x1 = X(a.x1); const y1 = Y(a.y1); const x2 = X(a.x2); const y2 = Y(a.y2)
        ops.push({ op: 'line', x1, y1, x2, y2, color: a.color ?? INK, width: a.width ?? 0.5 })
        ops.push(...arrowHead(x1, y1, x2, y2, a.head ?? 2.4, a.color ?? INK))
        if (a.text) {
          // Above the line, at its midpoint, haloed so it survives a mid-tone
          // render underneath.
          ops.push({
            op: 'text', x: (x1 + x2) / 2, y: Math.min(y1, y2) - 1.6, text: a.text,
            size: a.size ?? 11, weight: a.weight ?? 'bold', align: 'center',
            color: a.color ?? INK, halo: true,
          })
        }
        break
      }
      // A dimension is the measure page's whole vocabulary (p13): two witness
      // lines, an extension line arrowed at BOTH ends, and the value alongside.
      //
      // Both the witness lines and the label FOLLOW THE LINE's direction. They
      // used to be hard-coded — witness always horizontal, label always rotated
      // 90° — which is right for the reference's vertical stacks down a lateral
      // view and wrong for everything else. On a horizontal dimension the
      // witness lines lay along the dimension line itself (invisible, drawing
      // nothing) and the value read vertically across the artwork.
      case 'dimension': {
        const x1 = X(a.x1); const y1 = Y(a.y1); const x2 = X(a.x2); const y2 = Y(a.y2)
        const col = a.color ?? INK
        const w = a.width ?? 0.5
        const wit = a.witness ?? 0.06          // witness length, fraction of frame width
        const vertical = Math.abs(y2 - y1) > Math.abs(x2 - x1)
        ops.push({ op: 'line', x1, y1, x2, y2, color: col, width: w })
        ops.push(...arrowHead(x2, y2, x1, y1, a.head ?? 2.2, col))
        ops.push(...arrowHead(x1, y1, x2, y2, a.head ?? 2.2, col))
        // Witness lines run PERPENDICULAR to the dimension, which is what makes
        // them read as the two things being measured between.
        const half = wit * box.w / 2
        for (const [wx, wy] of [[x1, y1], [x2, y2]]) {
          ops.push(vertical
            ? { op: 'line', x1: wx - half, y1: wy, x2: wx + half, y2: wy, color: col, width: w }
            : { op: 'line', x1: wx, y1: wy - half, x2: wx, y2: wy + half, color: col, width: w })
        }
        if (a.text) {
          // Upright above a horizontal dimension; rotated alongside a vertical
          // one, as the reference has it. `angle` still overrides both.
          const angle = a.angle ?? (vertical ? 90 : 0)
          ops.push(vertical
            ? {
              op: 'text', x: (x1 + x2) / 2 - 1.5, y: (y1 + y2) / 2, text: a.text,
              size: a.size ?? 13, weight: a.weight ?? 'normal', align: 'left',
              color: col, angle, halo: true,
            }
            : {
              op: 'text', x: (x1 + x2) / 2, y: Math.min(y1, y2) - 2, text: a.text,
              size: a.size ?? 13, weight: a.weight ?? 'normal', align: 'center',
              color: col, angle, halo: true,
            })
        }
        break
      }
      default:
        // angleArc / inset are still unbuilt. Unknown kinds are dropped rather
        // than half-drawn, but never silently — see unsupportedAnnotations().
        break
    }
  }
  return ops
}

/** Annotation kinds present in the document that this build cannot draw yet. */
export function unsupportedAnnotations(doc) {
  const DRAWABLE = new Set(['text', 'line', 'leader', 'circle', 'arrow', 'dimension'])
  const found = new Set()
  const scan = (list) => { for (const a of list ?? []) if (!DRAWABLE.has(a.kind)) found.add(a.kind) }
  for (const page of doc.pages ?? []) {
    scan(page.annotations)
    for (const cell of page.cells ?? []) scan(cell.annotations)
    for (const step of page.steps ?? []) scan(step.annotations)
  }
  return [...found]
}

// ── helpers ──────────────────────────────────────────────────────────────────

/** Greedy word wrap against injected metrics. Long unbreakable words overflow rather than vanish. */
export function wrap(text, maxWidth, size, weight, measureText) {
  const words = String(text ?? '').split(/\s+/).filter(Boolean)
  if (!words.length) return ['']
  const lines = []
  let line = words[0]
  for (const word of words.slice(1)) {
    const candidate = `${line} ${word}`
    if (measureText(candidate, size, weight) <= maxWidth) line = candidate
    else { lines.push(line); line = word }
  }
  lines.push(line)
  return lines
}
